import type { ComponentType } from 'react'

import { DEFAULT_MAX_FILE_SIZE_MB } from 'app/utils/file/validation'
import { useFileUpload } from 'app/utils/hooks/useFileUpload'

interface FileUploadProps {
  fileUpload: ReturnType<typeof useFileUpload>
}

/**
 * Higher-Order Component that provides file upload functionality to class components
 *
 * This HOC wraps the useFileUpload hook and passes its values as props to the wrapped component.
 * This allows class components to use hook-based file upload functionality.
 *
 * @param Component - The component to wrap with file upload functionality
 * @returns Wrapped component with fileUpload prop injected
 */
export function withFileUpload<P extends object>(
  Component: ComponentType<P & FileUploadProps>,
): ComponentType<P> {
  function FileUploadWrapper(props: P) {
    const fileUploadProps = useFileUpload({
      maxSizeMB: DEFAULT_MAX_FILE_SIZE_MB,
      convertToBase64: true,
      showNotifications: true,
    })

    return <Component {...props} fileUpload={fileUploadProps} />
  }

  FileUploadWrapper.displayName = `withFileUpload(${
    Component.displayName || Component.name || 'Component'
  })`

  return FileUploadWrapper
}
